import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import {
  Plus, Loader2, Trash2, Eye, ExternalLink, BarChart3, Copy, 
  Pencil, Globe, FileText, Zap, Users, ArrowLeft, Search,
  MousePointerClick, UserPlus, TrendingUp, Link2, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMarketingManager() {
  const navigate = useNavigate();
  const token = localStorage.getItem('eventism_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [pages, setPages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pages'); // pages, leads
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [services, setServices] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [copiedSlug, setCopiedSlug] = useState(null);

  // Create form state
  const [createForm, setCreateForm] = useState({
    brief: '',
    page_type: 'landing',
    cta_types: ['lead_form'],
    standalone: true,
    service_ids: [],
    target_audience: '',
    offer: '',
    coupon_code: ''
  });

  const fetchPages = useCallback(async () => {
    try {
      const t = localStorage.getItem('eventism_token');
      const res = await axios.get(`${API}/admin/marketing/pages`, { headers: { Authorization: `Bearer ${t}` } });
      setPages(res.data.pages || []);
    } catch {
      toast.error('Failed to load marketing pages');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const t = localStorage.getItem('eventism_token');
      const res = await axios.get(`${API}/admin/marketing/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(res.data.leads || []);
    } catch {
      console.error('Failed to load leads');
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const t = localStorage.getItem('eventism_token');
      const res = await axios.get(`${API}/services/list-for-marketing`, { headers: { Authorization: `Bearer ${t}` } });
      setServices(res.data.services || []);
    } catch {
      console.error('Failed to load services');
    }
  }, []);

  useEffect(() => {
    fetchPages();
    fetchLeads();
    fetchServices();
  }, [fetchPages, fetchLeads, fetchServices]);

  const handleGenerate = async () => {
    if (!createForm.brief.trim()) {
      toast.error('Please describe what you want the page to promote');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/marketing/generate`, createForm, { headers });
      toast.success('Marketing page generated!');
      setShowCreateModal(false);
      setCreateForm({ brief: '', page_type: 'landing', cta_types: ['lead_form'], standalone: true, service_ids: [], target_audience: '', offer: '', coupon_code: '' });
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate page');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (pageId) => {
    try {
      await axios.put(`${API}/admin/marketing/pages/${pageId}`, { status: 'published' }, { headers });
      toast.success('Page published!');
      fetchPages();
    } catch {
      toast.error('Failed to publish');
    }
  };

  const handleUnpublish = async (pageId) => {
    try {
      await axios.put(`${API}/admin/marketing/pages/${pageId}`, { status: 'draft' }, { headers });
      toast.success('Page unpublished');
      fetchPages();
    } catch {
      toast.error('Failed to unpublish');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Delete this marketing page?')) return;
    try {
      await axios.delete(`${API}/admin/marketing/pages/${pageId}`, { headers });
      toast.success('Page deleted');
      fetchPages();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const copyUrl = (page) => {
    const url = `${window.location.origin}/go/${page.slug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(page.page_id);
    toast.success('URL copied!');
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const copyShortUrl = (page) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${page.short_url}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(page.page_id + '_short');
    toast.success('Short URL copied!');
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const toggleCta = (type) => {
    setCreateForm(prev => ({
      ...prev,
      cta_types: prev.cta_types.includes(type)
        ? prev.cta_types.filter(t => t !== type)
        : [...prev.cta_types, type]
    }));
  };

  const toggleService = (serviceId) => {
    setCreateForm(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId]
    }));
  };

  const filteredPages = pages.filter(p =>
    !searchFilter || p.title?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    p.brief?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const totalViews = pages.reduce((sum, p) => sum + (p.analytics?.views || 0), 0);
  const totalConversions = pages.reduce((sum, p) => sum + (p.analytics?.conversions || 0), 0);
  const overallConvRate = totalViews > 0 ? ((totalConversions / totalViews) * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" data-testid="marketing-title">Marketing Pages</h1>
              <p className="text-sm text-muted-foreground">AI-powered landing & sales pages for your campaigns</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-page-btn">
            <Zap className="mr-2 h-4 w-4" /> Generate Page
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{pages.length}</p>
              <p className="text-xs text-muted-foreground">Total Pages</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{totalViews}</p>
              <p className="text-xs text-muted-foreground">Total Views</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{totalConversions}</p>
              <p className="text-xs text-muted-foreground">Conversions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{overallConvRate}%</p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button variant={activeTab === 'pages' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('pages')} data-testid="tab-pages">
            <Globe className="mr-1 h-4 w-4" /> Pages ({pages.length})
          </Button>
          <Button variant={activeTab === 'leads' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('leads')} data-testid="tab-leads">
            <UserPlus className="mr-1 h-4 w-4" /> Leads ({leads.length})
          </Button>
        </div>

        {/* Pages Tab */}
        {activeTab === 'pages' && (
          <>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search pages..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-10"
                  data-testid="search-pages"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" /></div>
            ) : filteredPages.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Marketing Pages Yet</h3>
                  <p className="text-muted-foreground mb-4">Generate your first AI-powered landing page in seconds</p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Create Your First Page
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredPages.map(page => (
                  <Card key={page.page_id} className="hover:shadow-md transition-shadow" data-testid={`page-card-${page.page_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold truncate">{page.title}</h3>
                            <Badge variant={page.status === 'published' ? 'default' : 'secondary'} className="text-xs">
                              {page.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs capitalize">{page.page_type}</Badge>
                            {page.standalone && <Badge variant="outline" className="text-xs">Standalone</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground truncate mb-2">{page.brief}</p>

                          {/* URLs */}
                          <div className="flex items-center gap-3 text-xs flex-wrap">
                            <button onClick={() => copyUrl(page)} className="flex items-center gap-1 text-indigo-500 hover:text-indigo-600">
                              {copiedSlug === page.page_id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                              /go/{page.slug}
                            </button>
                            <button onClick={() => copyShortUrl(page)} className="flex items-center gap-1 text-purple-500 hover:text-purple-600">
                              {copiedSlug === page.page_id + '_short' ? <Check className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
                              {page.short_url}
                            </button>
                            {page.cta_types?.map(ct => (
                              <Badge key={ct} variant="outline" className="text-[10px] capitalize">{ct.replace('_', ' ')}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Analytics mini */}
                        <div className="flex items-center gap-4 text-center flex-shrink-0">
                          <div>
                            <p className="text-lg font-bold">{page.analytics?.views || 0}</p>
                            <p className="text-[10px] text-muted-foreground">Views</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-emerald-600">{page.analytics?.conversions || 0}</p>
                            <p className="text-[10px] text-muted-foreground">Conv.</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-amber-600">
                              {page.analytics?.views > 0 ? ((page.analytics?.conversions / page.analytics?.views) * 100).toFixed(0) : 0}%
                            </p>
                            <p className="text-[10px] text-muted-foreground">Rate</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => window.open(`/go/${page.slug}?preview=${page.page_id}`, '_blank')} title="Preview" data-testid={`preview-${page.page_id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {page.status === 'draft' ? (
                            <Button variant="ghost" size="sm" onClick={() => handlePublish(page.page_id)} data-testid={`publish-${page.page_id}`}>
                              Publish
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleUnpublish(page.page_id)} data-testid={`unpublish-${page.page_id}`}>
                              Unpublish
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(page.page_id)} className="text-destructive" data-testid={`delete-${page.page_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Marketing Leads</CardTitle>
              <CardDescription>{leads.length} leads captured</CardDescription>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No leads yet. Publish a page and share it!</p>
              ) : (
                <div className="space-y-2">
                  {leads.map(lead => (
                    <div key={lead.lead_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50" data-testid={`lead-${lead.lead_id}`}>
                      <div>
                        <p className="font-medium">{lead.name || 'Unknown'}</p>
                        <p className="text-sm text-muted-foreground">{lead.email}</p>
                        {lead.phone && <p className="text-xs text-muted-foreground">{lead.phone}</p>}
                      </div>
                      <div className="text-right text-sm">
                        {lead.event_date && <p>{lead.event_date}</p>}
                        {lead.guest_count > 0 && <p className="text-xs text-muted-foreground">{lead.guest_count} guests</p>}
                        {lead.coupon_code && <Badge variant="outline" className="text-xs">{lead.coupon_code}</Badge>}
                        <p className="text-xs text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Generate Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-indigo-500" /> Generate Marketing Page</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Brief */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">What should this page promote?</label>
              <Textarea
                placeholder="e.g., Summer wedding packages in Denver — 20% off catering when booked by May 31st"
                value={createForm.brief}
                onChange={(e) => setCreateForm(prev => ({ ...prev, brief: e.target.value }))}
                rows={3}
                data-testid="brief-input"
              />
              {/* Popular Upcoming Dates */}
              <div className="mt-3">
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Popular Upcoming Dates — click to add to brief</label>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const dates = [];
                    const now = new Date();
                    const holidays = [
                      { name: "Valentine's Day", month: 1, day: 14 },
                      { name: "St. Patrick's Day", month: 2, day: 17 },
                      { name: "Cinco de Mayo", month: 4, day: 5 },
                      { name: "Mother's Day", month: 4, day: 11 },
                      { name: "Memorial Day Weekend", month: 4, day: 26 },
                      { name: "Father's Day", month: 5, day: 15 },
                      { name: "4th of July", month: 6, day: 4 },
                      { name: "Labor Day Weekend", month: 8, day: 1 },
                      { name: "Halloween", month: 9, day: 31 },
                      { name: "Thanksgiving", month: 10, day: 27 },
                      { name: "Holiday Party Season", month: 11, day: 1 },
                      { name: "Christmas", month: 11, day: 25 },
                      { name: "New Year's Eve", month: 11, day: 31 },
                      { name: "Super Bowl Sunday", month: 1, day: 8 },
                      { name: "Graduation Season", month: 4, day: 15 },
                      { name: "Back to School", month: 7, day: 15 },
                      { name: "Prom Season", month: 3, day: 15 },
                      { name: "Corporate Q1 Kickoff", month: 0, day: 15 },
                      { name: "Corporate Q2 Kickoff", month: 3, day: 1 },
                      { name: "Corporate Q3 Kickoff", month: 6, day: 1 },
                      { name: "Corporate Q4 Kickoff", month: 9, day: 1 },
                      { name: "Summer BBQ Season", month: 5, day: 1 },
                      { name: "Fall Festival Season", month: 8, day: 15 },
                      { name: "Wedding Season", month: 5, day: 1 },
                    ];
                    const threeMonthsOut = new Date(now);
                    threeMonthsOut.setMonth(threeMonthsOut.getMonth() + 3);
                    holidays.forEach(h => {
                      let d = new Date(now.getFullYear(), h.month, h.day);
                      if (d < now) d = new Date(now.getFullYear() + 1, h.month, h.day);
                      if (d <= threeMonthsOut && d >= now) {
                        dates.push({ ...h, date: d });
                      }
                    });
                    dates.sort((a, b) => a.date - b.date);
                    return dates.slice(0, 12).map(d => (
                      <Button
                        key={d.name}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setCreateForm(prev => ({
                          ...prev,
                          brief: prev.brief ? `${prev.brief}\n${d.name} (${d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : `${d.name} (${d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
                        }))}
                        data-testid={`date-btn-${d.name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {d.name} — {d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </Button>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Page Type */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Page Type</label>
              <div className="flex gap-2">
                <Button type="button" variant={createForm.page_type === 'landing' ? 'default' : 'outline'} size="sm" onClick={() => setCreateForm(prev => ({ ...prev, page_type: 'landing' }))} data-testid="type-landing">
                  Landing Page
                </Button>
                <Button type="button" variant={createForm.page_type === 'sales' ? 'default' : 'outline'} size="sm" onClick={() => setCreateForm(prev => ({ ...prev, page_type: 'sales' }))} data-testid="type-sales">
                  Sales Page
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {createForm.page_type === 'landing' ? 'Short, focused — one clear CTA' : 'Long-form with storytelling, testimonials, FAQ, and multiple sections'}
              </p>
            </div>

            {/* CTA Types */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Call-to-Action Types</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'lead_form', label: 'Lead Capture Form', icon: UserPlus },
                  { id: 'book_now', label: 'Book Now / Get Quote', icon: MousePointerClick },
                  { id: 'coupon', label: 'Special Offer / Coupon', icon: TrendingUp }
                ].map(({ id, label, icon: Icon }) => (
                  <label key={id} className={`flex items-center gap-2 cursor-pointer border rounded-lg p-2.5 text-sm transition-colors ${createForm.cta_types.includes(id) ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-indigo-300'}`}>
                    <Checkbox checked={createForm.cta_types.includes(id)} onCheckedChange={() => toggleCta(id)} />
                    <Icon className="h-4 w-4" />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Coupon code if coupon CTA */}
            {createForm.cta_types.includes('coupon') && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Coupon Code</label>
                <Input
                  placeholder="e.g., SUMMER20"
                  value={createForm.coupon_code}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, coupon_code: e.target.value.toUpperCase() }))}
                  data-testid="coupon-input"
                />
              </div>
            )}

            {/* Page Display */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Display Style</label>
              <div className="flex gap-2">
                <Button type="button" variant={createForm.standalone ? 'default' : 'outline'} size="sm" onClick={() => setCreateForm(prev => ({ ...prev, standalone: true }))} data-testid="standalone-yes">
                  Standalone (no nav)
                </Button>
                <Button type="button" variant={!createForm.standalone ? 'default' : 'outline'} size="sm" onClick={() => setCreateForm(prev => ({ ...prev, standalone: false }))} data-testid="standalone-no">
                  Embedded (with nav)
                </Button>
              </div>
            </div>

            {/* Target Audience */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Target Audience (optional)</label>
              <Input
                placeholder="e.g., Engaged couples planning Denver weddings"
                value={createForm.target_audience}
                onChange={(e) => setCreateForm(prev => ({ ...prev, target_audience: e.target.value }))}
                data-testid="audience-input"
              />
            </div>

            {/* Offer */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Special Offer (optional)</label>
              <Input
                placeholder="e.g., 20% off when you book by May 31st"
                value={createForm.offer}
                onChange={(e) => setCreateForm(prev => ({ ...prev, offer: e.target.value }))}
                data-testid="offer-input"
              />
            </div>

            {/* Services to feature */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Feature Services (optional)</label>
              <p className="text-xs text-muted-foreground mb-2">Select services to include with images on the page</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-2">
                {services.map(svc => (
                  <label
                    key={svc.service_id}
                    className={`flex items-center gap-2 cursor-pointer rounded-lg p-2 text-xs transition-colors ${createForm.service_ids.includes(svc.service_id) ? 'bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-500' : 'border border-transparent hover:bg-muted'}`}
                  >
                    <Checkbox
                      checked={createForm.service_ids.includes(svc.service_id)}
                      onCheckedChange={() => toggleService(svc.service_id)}
                    />
                    <div className="truncate">
                      <p className="font-medium truncate">{svc.name}</p>
                      <p className="text-muted-foreground capitalize">{svc.category}</p>
                    </div>
                  </label>
                ))}
              </div>
              {createForm.service_ids.length > 0 && (
                <p className="text-xs text-indigo-500 mt-1">{createForm.service_ids.length} service(s) selected</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating || !createForm.brief.trim()} data-testid="generate-btn">
              {generating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <><Zap className="mr-2 h-4 w-4" /> Generate with AI</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
